import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { TabBarIndicator } from 'react-native-tab-view';

import { LeftSideTabBar, LeftSideTabBarProps } from '@/components/LeftSideTabBar';
import { Box } from '@/components/lib/components';
import { useGQLRequestPolicy } from '@/feature/guest-user/hooks/use-gql-request-policy';
import { useLobbySectionSelection } from '@/feature/lobby/hooks/use-lobby-section-selection';
import { useGetOrderedLeaguesByEvents } from '@/hooks/use-fantasy-league-configs';
import { useResumeEffect } from '@/hooks/use-resume';
import { designSystem } from '@/styles/styles';

import { PickemLeagueTile } from './PickemLeagueTile';
import { PickemLobbyLaunchpadTile } from './PickemLobbyLaunchpadTile';
import { LobbyLeagueSkeleton } from './Skeletons/LobbyLeagueSkeleton';

const skeletonArray = Array.from({ length: 16 }, (_, index) => ({
    key: `loading-${index}`,
    id: `${index}`,
    title: `${index}`,
}));

const LEAGUES_LIST_PADDING = 16;

export const LobbyLeagues = () => {
    const requestPolicy = useGQLRequestPolicy('cache-and-network');
    const { fetching, isSorting, refetch: reload, sortedLeagues } = useGetOrderedLeaguesByEvents(false, requestPolicy);

    const selectedTile = useLobbySectionSelection(state => state.selectedTile);

    const [tabIndex, setTabIndex] = useState(0);

    useResumeEffect(reload);

    const leagues = useMemo(() => {
        return fetching || isSorting
            ? skeletonArray
            : [
                  { key: 'lobby', id: 'lobby', title: 'Lobby' },
                  ...sortedLeagues.map(item => ({
                      key: item.league,
                      id: item.league.label,
                      title: item.league.label,
                  })),
              ];
    }, [fetching, isSorting, sortedLeagues]);

    useEffect(() => {
        // we need to change the tab index if the selectedTile changes from the outside
        if (selectedTile) {
            const index = leagues.findIndex(item => item.id === selectedTile);
            // we need to check if the index is different from the current tabIndex to avoid redundant updates
            if (index !== -1 && index !== tabIndex) {
                setTabIndex(index);
            }
        }
    }, [leagues, selectedTile, tabIndex]);

    const setSelectedTab = useCallback(
        (id: string) => {
            setTabIndex(leagues.findIndex(item => item.id === id));
        },
        [leagues]
    );

    return (
        <LeftSideTabBar
            containerStyle={styles.container}
            indicatorStyle={styles.indicator}
            tabBarStyle={styles.container}
            tabs={leagues}
            initialIndex={tabIndex}
            renderIndicator={LeaguesIndicator}
            setSelectedTab={setSelectedTab}
            // the gap is managed by each item + custom styling
            gap={0}
            location="lobby-leagues"
            renderTabBarItem={({ onLayout, onPress, route, key, navigationState }) => {
                if (fetching || isSorting) {
                    return <LobbyLeagueSkeleton />;
                }
                if (route.key === 'lobby') {
                    return <PickemLobbyLaunchpadTile onLayout={onLayout} updateTab={onPress} />;
                }

                const item = sortedLeagues.find(it => it.league.label === route.key);

                if (!item) {
                    return <Box />;
                }

                const isLastItem = key === navigationState.routes[navigationState.routes.length - 1].key;

                return (
                    <PickemLeagueTile
                        isLastItem={isLastItem}
                        updateTab={onPress}
                        onLayout={onLayout}
                        {...item.league}
                        eventsCount={item.eventsCount}
                        selected={selectedTile === item.league.label}
                        isLive={item.hasLiveEvents}
                    />
                );
            }}
        />
    );
};

/**
 * Custom indicator for the Leagues List.
 *
 * In the design, the tabs have a gap of 16, and the list has a horizontal padding of 16.
 * We couldn't find a way to achieve this solely with TabView props while maintaining correct spacing
 * and alignment of the indicator
 *
 * To work around this, a paddingLeft is added to each item,
 * except for the last one which gets both paddingLeft and paddingRight
 * This results in an incorrect tab indicator width.
 *
 * To adjust, we render the default indicator with a custom width
 *
 */
const LeaguesIndicator: LeftSideTabBarProps['renderIndicator'] = indicatorProps => {
    const { index, routes } = indicatorProps.navigationState;

    const width = indicatorProps.getTabWidth(index);
    const isLastItem = index === routes.length - 1;

    const paddingHorizontal = isLastItem ? LEAGUES_LIST_PADDING * 2 : LEAGUES_LIST_PADDING;

    const correctedWidth = width > 0 ? width - paddingHorizontal : 0;
    return <TabBarIndicator {...indicatorProps} width={correctedWidth} />;
};

export const LEAGUES_LIST_HEIGHT = 100;

const styles = StyleSheet.create({
    indicator: {
        left: LEAGUES_LIST_PADDING,
    },
    container: {
        height: LEAGUES_LIST_HEIGHT,
        marginHorizontal: 0,
        borderBottomWidth: 1,
        borderBottomColor: designSystem.colors.neutral800,
        borderStyle: 'solid',
    },
});
